import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Save, RotateCcw, Eye, Code, Variable } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EmailTemplateEditor = () => {
  const { templateType } = useParams();
  const navigate = useNavigate();
  const bodyRef = useRef(null);

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [showPreview, setShowPreview] = useState(true);

  // Editable fields
  const [subject, setSubject] = useState('');
  const [headerTitle, setHeaderTitle] = useState('');
  const [headerColor, setHeaderColor] = useState('#004E89');
  const [buttonColor, setButtonColor] = useState('#004E89');
  const [bodyHtml, setBodyHtml] = useState('');
  const [footerText, setFooterText] = useState('');

  useEffect(() => {
    fetchTemplate();
  }, [templateType]);

  const fetchTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/email-templates/${templateType}`);
      const t = res.data;
      setTemplate(t);
      setSubject(t.subject || '');
      setHeaderTitle(t.header_title || '');
      setHeaderColor(t.header_color || '#004E89');
      setButtonColor(t.button_color || '#004E89');
      setBodyHtml(t.body_html || '');
      setFooterText(t.footer_text || '');
    } catch (err) {
      toast.error('Error al cargar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/email-templates/${templateType}/preview`);
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
    } catch {
      // silent
    }
  }, [templateType]);

  // Build local preview from current form state
  useEffect(() => {
    const sampleData = {
      nombre_usuario: 'Juan Pérez', nombre_alumno: 'Juan Pérez',
      nombre_curso: 'Soporte Vital Básico - Ejemplo', rol: 'Alumno',
      email: 'juan.perez@ejemplo.com', password_temporal: 'TempPass123',
      url_login: '#', url_cursos: '#', url_curso: '#', url_encuesta: '#',
      url_reset: '#', url_tema: '#',
      fechas_curso: '15/03/2026 - 20/03/2026', lugar: 'Espai La Senieta, Teulada',
      enlace_meet: 'https://meet.google.com/abc-def-ghi',
      fecha_sesion: '15/03/2026', hora_sesion: '09:00 - 14:00', horas_antes: '24',
      tipo_certificado: 'Certificado de Aprovechamiento', codigo_verificacion: 'ABC-123-XYZ',
      nuevo_estado: 'APTO', emoji_estado: '✅', color_estado: '#28a745',
      bg_color_estado: '#d4edda', mensaje_estado: '¡Felicidades! Has superado satisfactoriamente el curso.',
      info_adicional: '<p>Tu certificado estará disponible próximamente.</p>',
      titulo_anuncio: 'Cambio de horario', contenido_anuncio: 'La sesión 3 se adelanta al viernes.',
      autor: 'Dr. García', autor_respuesta: 'María López', autor_tema: 'Carlos Ruiz',
      titulo_tema: 'Dudas sobre el módulo 2', nombre_empresa: 'IRTA Formación',
      mensaje_rol: 'Has sido inscrito como alumno en nuestra plataforma de formación.',
      seccion_credenciales: '<div style="background-color:#fff3cd;padding:15px;border-radius:5px;margin:20px 0;border-left:4px solid #ffc107;"><p><strong>Credenciales:</strong></p><p>Email: <strong>juan.perez@ejemplo.com</strong></p><p>Contraseña: <strong>TempPass123</strong></p></div>',
      info_extra: '<div style="background-color:#e8f4f8;padding:15px;border-radius:5px;margin:15px 0;"><p><strong>Fechas:</strong> 15/03/2026 - 20/03/2026</p></div>',
      info_ubicacion: '<p><strong>Lugar:</strong> Espai La Senieta, Teulada</p>',
      seccion_verificacion: '<div style="background-color:#e8f4f8;padding:15px;border-radius:5px;margin:15px 0;text-align:center;"><p style="font-size:12px;color:#666;">Código:</p><p style="font-family:monospace;font-size:16px;"><strong>ABC-123-XYZ</strong></p></div>',
    };

    let rendered = bodyHtml;
    let renderedSubject = subject;
    Object.entries(sampleData).forEach(([key, value]) => {
      const re = new RegExp(`\\{${key}\\}`, 'g');
      rendered = rendered.replace(re, value);
      renderedSubject = renderedSubject.replace(re, value);
    });

    const html = `<!DOCTYPE html><html><head><style>
      body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin:0; }
      .container { max-width: 600px; margin: 0 auto; }
      .header { background-color: ${headerColor}; color: white; padding: 20px; text-align: center; }
      .content { padding: 20px; background-color: #f9f9f9; }
      .button { display: inline-block; padding: 12px 24px; background-color: ${buttonColor}; color: white; text-decoration: none; border-radius: 5px; margin: 20px 0; }
      .footer { padding: 20px; text-align: center; font-size: 12px; color: #666; }
    </style></head><body>
      <div class="container">
        <div class="header"><h1>${headerTitle}</h1></div>
        <div class="content">${rendered}</div>
        <div class="footer"><p>${footerText}</p></div>
      </div>
    </body></html>`;

    setPreviewHtml(html);
    setPreviewSubject(renderedSubject);
  }, [bodyHtml, subject, headerTitle, headerColor, buttonColor, footerText]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/email-templates/${templateType}`, {
        subject,
        header_title: headerTitle,
        header_color: headerColor,
        button_color: buttonColor,
        body_html: bodyHtml,
        footer_text: footerText,
      });
      toast.success('Plantilla guardada correctamente');
    } catch (err) {
      toast.error('Error al guardar la plantilla');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restaurar la plantilla a los valores por defecto? Se perderán todos los cambios.')) return;
    try {
      await axios.post(`${API_URL}/email-templates/${templateType}/reset`);
      toast.success('Plantilla restaurada');
      fetchTemplate();
    } catch {
      toast.error('Error al restaurar');
    }
  };

  const insertVariable = (varKey) => {
    if (!bodyRef.current) return;
    const ta = bodyRef.current;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = bodyHtml;
    const insertion = `{${varKey}}`;
    setBodyHtml(text.substring(0, start) + insertion + text.substring(end));
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + insertion.length;
    }, 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  if (!template) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Plantilla no encontrada</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="email-template-editor">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/email-templates')} data-testid="template-editor-back">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="font-bold text-xl text-slate-900">{template.name}</h1>
              <p className="text-sm text-slate-500">{template.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset} data-testid="template-reset-btn">
              <RotateCcw className="w-4 h-4 mr-2" />
              Restaurar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#004E89] hover:bg-[#003B66]" data-testid="template-save-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: Editor */}
          <div className="space-y-4">
            {/* Design settings */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Diseño
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Título cabecera</Label>
                  <Input value={headerTitle} onChange={(e) => setHeaderTitle(e.target.value)} data-testid="template-header-title" />
                </div>
                <div>
                  <Label>Texto pie de página</Label>
                  <Input value={footerText} onChange={(e) => setFooterText(e.target.value)} data-testid="template-footer-text" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Color cabecera</Label>
                  <div className="flex gap-2 mt-1">
                    <input type="color" value={headerColor} onChange={(e) => setHeaderColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" data-testid="template-header-color" />
                    <Input value={headerColor} onChange={(e) => setHeaderColor(e.target.value)} className="flex-1 font-mono text-sm" />
                  </div>
                </div>
                <div>
                  <Label>Color botón</Label>
                  <div className="flex gap-2 mt-1">
                    <input type="color" value={buttonColor} onChange={(e) => setButtonColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" data-testid="template-button-color" />
                    <Input value={buttonColor} onChange={(e) => setButtonColor(e.target.value)} className="flex-1 font-mono text-sm" />
                  </div>
                </div>
              </div>
            </div>

            {/* Subject */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-3">
              <h2 className="font-semibold text-slate-900">Asunto del email</h2>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Asunto..." data-testid="template-subject" />
            </div>

            {/* Variables */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-3">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Variable className="w-4 h-4" /> Variables disponibles
              </h2>
              <p className="text-xs text-slate-500">Haz clic para insertar en el cuerpo del email</p>
              <div className="flex flex-wrap gap-2">
                {template.variables?.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => insertVariable(v.key)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-blue-100 hover:text-blue-700 text-slate-700 rounded-full text-xs font-mono transition-colors border border-slate-200 hover:border-blue-300"
                    title={v.label}
                    data-testid={`var-btn-${v.key}`}
                  >
                    {`{${v.key}}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Body HTML */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 space-y-3">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Code className="w-4 h-4" /> Cuerpo del email (HTML)
              </h2>
              <Textarea
                ref={bodyRef}
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                rows={16}
                className="font-mono text-sm leading-relaxed"
                data-testid="template-body-html"
              />
            </div>
          </div>

          {/* Right: Preview */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden sticky top-4">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Vista previa
                </h2>
                <span className="text-xs text-slate-500">Datos de ejemplo</span>
              </div>
              {previewSubject && (
                <div className="px-5 py-2 border-b border-slate-100 bg-slate-50/50">
                  <p className="text-xs text-slate-500">Asunto:</p>
                  <p className="text-sm font-medium text-slate-800">{previewSubject}</p>
                </div>
              )}
              <div className="p-0" data-testid="template-preview">
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full border-0"
                  style={{ height: '600px' }}
                  sandbox=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmailTemplateEditor;
